import React, { useCallback, useEffect, useState } from 'react'
import { Main, MainBody, MainTop, WhiteBox } from '@/components/Layout'
import { FormLoader, PropertyRenderer } from '@/components/changeRequests'
import context from '@/context'
import { ValidationBoundary } from '@/components/validation'
import { TabContent, TabNavItems, TabPane } from '@/components/tabs'
import { AddressesInstitution } from '@/components/demographics/addresses'
import { Licenses } from '@/components/demographics/licenses'
import { InstitutionContacts } from '@/components/demographics/contacts'
import { InstitutionGroups } from '@/components/demographics/institutionGroups'
import { Branches } from '@/components/demographics/branches'
import { InstitutionRealEstates } from '@/components/demographics/realEstates'
import { IndividualsInCharge } from '@/components/demographics/individualsInCharge'
import { RelatedPersons } from '@/components/demographics/relatedPersons'
import { ScopeDetailHeader } from '@/components/changeRequests/scopeDetailHeader'
import { getBadges } from '@/components/changeRequests/badgeUtils'
import { CompanyBasicDetails } from '@/components/demographics/companyBasicDetails'
import { StatusHistory } from '@/components/demographics/statusHistory'
import { BankDealsHistory } from '@/components/demographics/bankDealsHistory'
import { useLocalization } from '@/hooks/localization'
import { InstitutionBlacklists } from '@/components/demographics/blacklists'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { InstitutionChangeRequestHistory } from '@/components/demographics/changeRequestHistory'
import { usePropertyReader } from '@/hooks/usePropertyReader'
import { useInstitutionPermissionChecker } from '@/hooks/useInstitutionPermissionsChecker'
import { permissionTypes } from '@/enums/permissionTypes'
import { institutionPermissionTypes } from '@/enums/institutionPermissionTypes'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { activeTaskSelector } from '@/components/changeRequests/selectors'

const CompanyDetail = React.memo(function CompanyDetail (props) {
  const loader = useCallback(id => context.api.companyService.get(id), [])
  const renderForm = useCallback(formProps => (<CompanyDetailForm {...formProps} />), [])
  return (
    <FormLoader {...props} loader={loader} render={renderForm} />
  )
})

const CompanyDetailForm = React.memo(function CompanyDetailForm (props) {
  const { targetId, mode, data, isChangeItemId } = props
  const [ institutionId ] = usePropertyReader(['institutionId'])
  const [ companyId ] = usePropertyReader(['id'])
  const { localizeProps } = useLocalization()
  const [activeTab, setActiveTab] = useState('1')
  const isGranted = usePermissionChecker()
  const getInstitutionPermission = useInstitutionPermissionChecker(institutionId, isChangeItemId)

  const getAddresses = useCallback(req => context.api.companyService.getAddresses(targetId, req), [targetId])
  const getLicenses = useCallback(req => context.api.companyService.getLicenses(targetId, req), [targetId])
  const getContacts = useCallback(req => context.api.companyService.getContacts(targetId, req), [targetId])
  const getInstitutionGroups = useCallback(req => context.api.companyService.getInstitutionGroups(targetId, req), [targetId])
  const getBranches = useCallback(req => context.api.companyService.getBranches(targetId, req), [targetId])
  const getRealEstates = useCallback(req => context.api.companyService.getRealEstates(targetId, req), [targetId])
  const getRealEstate = useCallback(id => context.api.companyService.getRealEstate(id), [])
  const getIndividualsInCharge = useCallback(req => context.api.companyService.getIndividualsInCharge(targetId, req), [targetId])
  const getRelatedPersons = useCallback(req => context.api.companyService.getRelatedPersons(targetId, req), [targetId])
  const getPersonsRelatedToThis = useCallback(req => context.api.companyService.getPersonsRelatedToThis(targetId, req), [targetId])
  const getStatusHistory = useCallback(req => context.api.companyService.getStatusHistory(targetId, req), [targetId])
  const getBankDealsHistory = useCallback(req => context.api.companyService.getBankDealsHistory(targetId, req), [targetId])
  const getChangeRequestHistory = useCallback(req => context.api.changeRequestService.getHistoryForEntity(targetId, 'Companies', req), [targetId])
  const [isClassCompany] = usePropertyReader(['isClassCompany'])

  const permissionPredicate = useCallback(x => !x.permission ||
    getInstitutionPermission(x.permission) !== permissionTypes.enum.None,
  [getInstitutionPermission])
  const hideReadonlyInInsertMode = useCallback(x => !x.readonly || targetId, [targetId])
  const canViewChangeHistory = isGranted('CanViewChangeHistory')
  const [companyStatus] = usePropertyReader(['status.code'])
  const hasValidStatus = companyStatus !== 'Merged'

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const fromCompanyId = queryParams.get('fromCompanyId')
  const fromCompanyActiveTab = queryParams.get('fromCompanyActiveTab')

  const backUrl = context.isInternalApp()
    ? (fromCompanyId ? `/companies/${fromCompanyId}?fromCompanyActiveTab=${fromCompanyActiveTab}` : '/companies')
    : ''

  useEffect(() => {
    if (fromCompanyActiveTab) {
      setActiveTab(fromCompanyActiveTab)
    }
  }, [fromCompanyActiveTab])

  const activeTask = useSelector(activeTaskSelector)
  const isMergeApproveTask = activeTask && activeTask.isMergeApproval && activeTask.isMergeApproval === true

  return (
    <Main>
      <MainTop>
        <ScopeDetailHeader isClassCompany={isClassCompany} hasValidStatus={hasValidStatus} title='Individuals.CompanyDetailTitle' backUrl={backUrl}>
          <PropertyRenderer name={localizeProps()} readonly />
        </ScopeDetailHeader>
      </MainTop>
      <MainBody transparent>
        <ValidationBoundary>
          <WhiteBox>
            <TabNavItems
              buttonView
              activeTabId={activeTab}
              onClick={setActiveTab}
              items={getBadges(mode, data, [
                { id: '1', text: 'Institutions.Details', badge: '' },
                { id: '2', text: 'Persons.Addresses', badge: 'addresses', permission: institutionPermissionTypes.enum.Addresses },
                { id: '3', text: 'Institutions.Licenses', badge: 'licenses', permission: institutionPermissionTypes.enum.Licenses },
                { id: '4', text: 'Persons.Contacts', badge: 'contacts', permission: institutionPermissionTypes.enum.Contacts },
                context.isInternalApp() && { id: '5', text: 'Institutions.Groups', badge: 'groups', readonly: true, permission: institutionPermissionTypes.enum.InstitutionGroups },
                { id: '6', text: 'Institutions.Branches', badge: 'branches', permission: institutionPermissionTypes.enum.Branches },
                { id: '7', text: 'Institutions.RealEstates', badge: 'realEstates', permission: institutionPermissionTypes.enum.RealEstates },
                context.isInternalApp() && { id: '8', text: 'Institutions.IndividualsInCharge', badge: 'individualsInChange', readonly: true, permission: institutionPermissionTypes.enum.IndividualsInCharge },
                { id: '9', text: 'Institutions.RelatedPersons', badge: 'relatedPersons', readonly: !context.isInternalApp(), permission: institutionPermissionTypes.enum.AssociatedPersons },
                { id: '10', text: 'Institutions.PersonsRelatedToThis', readonly: !isMergeApproveTask, permission: institutionPermissionTypes.enum.AssociatedToInstitutions },
                { id: '11', text: 'Institutions.StatusHistory', readonly: true, permission: institutionPermissionTypes.enum.StatusHistory },
                { id: '13', text: 'Institutions.BankDealsHistory', readonly: true, permission: institutionPermissionTypes.enum.BankDealsHistory },
                (canViewChangeHistory && !isChangeItemId) && { id: '14', text: 'Institutions.ChangeRequestHistory', readonly: true, permission: institutionPermissionTypes.enum.ChangeHistory },
                context.isInternalApp() && { id: '12', text: 'Blacklists.BlacklistTitle', badge: 'blacklists', readonly: true, permission: institutionPermissionTypes.enum.Blacklists }
              ].filter(permissionPredicate).filter(hideReadonlyInInsertMode).filter(Boolean))} />
            <TabContent activeTab={activeTab} renderActiveOnly>
              <TabPane tabId='1'>
                <CompanyBasicDetails />
              </TabPane>
              <TabPane tabId='2'>
                <AddressesInstitution dataSource={getAddresses} />
              </TabPane>
              <TabPane tabId='3'>
                <Licenses dataSource={getLicenses} />
              </TabPane>
              <TabPane tabId='4'>
                <InstitutionContacts dataSource={getContacts} />
              </TabPane>
              {context.isInternalApp() && <TabPane tabId='5'>
                <InstitutionGroups dataSource={getInstitutionGroups} />
              </TabPane>}
              <TabPane tabId='6'>
                <Branches dataSource={getBranches} />
              </TabPane>
              <TabPane tabId='7'>
                <InstitutionRealEstates dataSource={getRealEstates} detailDataSource={getRealEstate} />
              </TabPane>
              {context.isInternalApp() && <TabPane tabId='8'>
                <IndividualsInCharge dataSource={getIndividualsInCharge} />
              </TabPane>}
              <TabPane tabId='9'>
                <RelatedPersons nameField='person'
                  dataSource={getRelatedPersons}
                  readonly={!context.isInternalApp()}
                  permission={institutionPermissionTypes.enum.AssociatedPersons} />
              </TabPane>
              <TabPane tabId='10'>
                <RelatedPersons
                  dataName='associatedToInstitutions'
                  nameField='institution'
                  dataSource={getPersonsRelatedToThis}
                  permission={institutionPermissionTypes.enum.AssociatedToInstitutions}
                  readonly={!isMergeApproveTask}
                  currentInstitutionId={companyId}
                  activeTab={activeTab} />
              </TabPane>
              <TabPane tabId='11'>
                <StatusHistory dataSource={getStatusHistory} />
              </TabPane>
              <TabPane tabId='13'>
                <BankDealsHistory dataSource={getBankDealsHistory} />
              </TabPane>
              <TabPane tabId='14'>
                <InstitutionChangeRequestHistory dataSource={getChangeRequestHistory} />
              </TabPane>
              {context.isInternalApp() && <TabPane tabId='12'>
                <InstitutionBlacklists />
              </TabPane>}
            </TabContent>
          </WhiteBox>
        </ValidationBoundary>
      </MainBody>
    </Main>
  )
})

export {
  CompanyDetail
}



// WEBPACK FOOTER //
// ./src/components/demographics/companyDetail.js